import { IShippingRule } from "../features/shippingRules/shippingRulesSlice";
import {
  AdvancedFilterModel,
  FilterModel,
  SortModelItem,
} from "ag-grid-community";
import { IFeeSettingsState } from "../features/feeSettings/feeSettingsSlice";
import { axiosInstance } from "./config";

export const getKeepa = (productId: any, country: any, range: any) => {
  return axiosInstance.get(
    `/Keepa/KeepaPriceHistory/${productId}?Country=${country}&Range=${range}`,
    { responseType: "arraybuffer" },
  );
};

export const getProductVariations = (productId: any) => {
  return axiosInstance.get(`/Product/${productId}/ProductVariations`);
};

interface IGetProductsProps {
  productUploadId: string;
  fulfillment: string;
  page?: number;
  perPage?: number;
  returnFavoriteData?: boolean;
  returnPowerUpData?: boolean;
  filter?: FilterModel | AdvancedFilterModel | null;
  sort?: SortModelItem[] | null;
}

export const getProducts = async ({
  productUploadId,
  fulfillment,
  returnFavoriteData = false,
  returnPowerUpData = false,
  page = 1,
  perPage = 100,
  filter = {},
  sort = [],
}: IGetProductsProps) => {
  const res = await axiosInstance.post(
    `/ProductUpload/${productUploadId}/Products`,
    { page, perPage, filter, sort },
    {
      headers: {
        Fulfillment: fulfillment,
        ReturnFavoriteData: returnFavoriteData,
        ReturnPowerUpData: returnPowerUpData,
      },
    },
  );
  return res.data;
};

export const powerUpProduct = (productId: string) => {
  return axiosInstance.post(`/PowerUp/${productId}`);
};

export const powerUpProducts = (productIds: string[]) => {
  return axiosInstance.post(`/PowerUp/Data`, {
    productScanIds: productIds,
  });
};

export const addProductToFavorite = (
  productId: string,
  productUploadId: string,
) => {
  return axiosInstance.post(`/Product/AddToFavorite`, {
    productId,
    productUploadId,
  });
};

export const addProductsToFavorite = (
  productIds: string[],
  productUploadId: string,
) => {
  return axiosInstance.post(`/Product/AddToFavorites`, {
    productIds,
    productUploadId,
  });
};

export const deleteProductFromFavorite = (
  productId: string,
  productUploadId: string,
) => {
  return axiosInstance.delete(`/Product/DeleteFavorite`, {
    data: {
      productId,
      productUploadId,
    },
  });
};

export const deleteProducts = (
  productIds: string[],
  productUploadId: string,
) => {
  return axiosInstance.delete(`/Product/DeleteProducts`, {
    data: {
      productIds,
      productUploadId,
    },
  });
};

export const renameFileName = (
  productUploadId: string,
  newFileName: string,
) => {
  return axiosInstance.post(`/ProductUpload/RenameFileName`, {
    productUploadId,
    newFileName,
  });
};

export const getGraphDetails = (datas: any) => {
  const { country, asin, range } = datas;
  return axiosInstance.get(
    `/Keepa/KeepaPriceHistory/${asin}?Country=${country}&Range=${range}`,
    {
      responseType: "arraybuffer",
    },
  );
};

export const ProductUploadProcess = () => {
  return axiosInstance.post(`/ProductUpload/UploadProcess`);
};

export const getProductInfo = ({ asin, lookupId }: any) => {
  return axiosInstance.get(`/Analyzer/${asin}/QuickScan`, {
    params: {
      lookupId,
    },
  });
};

// export const getRanksAndPrices = (productId: any, range: any) => {
//   return axiosInstance.get(`/Analyzer/Ranks?ASIN=${productId}&Range=${range}`);
// };

export const getShippingRules = async (): Promise<IShippingRule[]> => {
  const res = await axiosInstance.get(`/InboundShipping`);
  return res.data?.shippingRules || [];
};

export const updateShippingRules = async (shippingRules: IShippingRule[]) => {
  return await axiosInstance.post(`/InboundShipping`, { shippingRules });
};

export const getScanSettings = () => {
  return axiosInstance.get(`/ScanSettings`);
};

export const updateScanSettings = ({
  inboundPlacementFeeType,
  storageMonth,
  prepFee,
  miscFee,
  miscFeePercentage,
  vat,
  vatPercentage,
}: IFeeSettingsState) => {
  return axiosInstance.post(`/ScanSettings`, {
    miscFee,
    miscFeePercentage,
    vat,
    vatPercentage,
    prepFeePerItem: prepFee,
    inboundPlacementFeeSplit: inboundPlacementFeeType,
    storageInMonths: storageMonth,
  });
};

export interface Preset {
  presetId: string;
  preset: string;
  configuration: string;
}

export const getPresets = async (): Promise<Preset[]> => {
  const response = await axiosInstance.get(`/preset`);
  return response.data;
};

export const createPreset = async (
  presetName: string,
  configuration: string,
): Promise<string> => {
  const payload = {
    preset: presetName,
    configuration: configuration,
  };
  const response = await axiosInstance.post(`/preset`, payload);
  return response.data;
};

export const getPresetById = async (presetId: string): Promise<Preset> => {
  const response = await axiosInstance.get(`/preset/${presetId}`);
  return response.data;
};

export const deletePreset = (presetId: string) => {
  return axiosInstance.delete(`/Preset/${presetId}`);
};

export const exportProducts = async ({
  productUploadId,
  fileName,
  filter,
  sort,
  page = 0,
  perPage = 1000000,
}: {
  productUploadId: string;
  fileName: string;
  page?: number;
  perPage?: number;
  filter?: FilterModel;
  sort?: any;
}) => {
  return await axiosInstance.post(
    `/ProductUpload/${productUploadId}/ExportProducts`,
    { page, perPage, filter, sort },
    {
      headers: {
        ExportFileName: fileName,
        CompressZip: false,
        IsFavoriteFile: false,
      },
      responseType: "blob",
    },
  );
};

export const getAverages = (asin: string) => {
  return axiosInstance.get(`/Analyzer/${asin}/UpdatedData`);
};

export const fetchDashboardInsight = (range: number) => {
  return axiosInstance.get(`/Dashboard/${range}/SearchInsights`);
};

export const fetchTopProducts = () => {
  return axiosInstance.get(`/Dashboard/TopProfitableProducts`);
};
